<template>
    <div>       
        <!-- Add Modal -->
        <b-modal
            v-model="createModal"
            title="Add Internal User"
            @hidden="onCreateModalHidden"
            centered
            size="md"
            :hide-footer="showCreateSuccess"
            content-class="bg-gray-50"
        >
            <div class="d-flex" style="min-height: 300px">
                <transition name="fade-fast" mode="out-in">
                    <save-success v-if="showCreateSuccess" :message="redirectAfterCreate ? createSuccessMessageRedirect : createSuccessMessage"></save-success>
                    <internal-tu-detail-form
                        v-else
                        ref="createForm"
                        :new-data="newData"
                        :hide-company-input="hideCompanyInput"
                        :company-data="companyData"
                        :allow-company-change="allowCompanyChange"
                        :enabled-role-options="enabledRoleOptionsCreate"
                        :all-role-options="allRoleOptions"
                        @save-complete="handleCreateComplete"
                        create-new
                        no-refresh-on-update
                        edit-mode
                        class="w-100"
                    ></internal-tu-detail-form>
                </transition>
            </div>
            <template v-slot:modal-footer="{ cancel }">
                <b-button variant="secondary" @click="cancel()">Cancel</b-button>
                <b-button variant="primary" @click="createItemForm">Add</b-button>
            </template>
        </b-modal>

        <!-- UPDATE MODAL -->
        <b-modal
            v-model="updateModal"
            title="Internal User Details"
            @hidden="onUpdateModalHidden"
            centered
            size="md"
            :hide-footer="showUpdateSuccess"
            content-class="bg-gray-50"
        >
            <div class="d-flex" style="min-height: 300px">
                <transition name="fade-fast" mode="out-in">
                    <save-success v-if="showUpdateSuccess" :message="updateSuccessMessage"></save-success>
                    <internal-tu-detail-form
                        v-else
                        :tu-id="updateItemId"
                        ref="updateForm"
                        :enabled-role-options="enabledRoleOptionsUpdate"
                        :all-role-options="allRoleOptions"
                        @update-complete="handleUpdateComplete"
                        @access-updated="handleInviteUpdate"
                        no-refresh-on-update
                        class="w-100"
                    ></internal-tu-detail-form>
                </transition>
            </div>
            <template v-slot:modal-footer="{ cancel }">
                <b-button v-if="editMode" variant="outline-secondary" @click="cancelEditMode">Cancel Edits</b-button>
                <b-button v-else variant="outline-secondary" @click="cancel()">Close</b-button>
                <b-button v-if="editMode" variant="success" @click="updateItemForm">Save Edits</b-button>
                <b-button v-else variant="secondary" @click="enterEditMode">Edit</b-button>
            </template>
        </b-modal>

    </div>
</template>

<script>
import InternalTuDetailForm from './InternalTuDetailForm';
import SaveSuccess from './functional-comps/SaveSuccess';

export default {
    name: 'InternalTuCreateUpdateModal',
    components: {
        InternalTuDetailForm,
        SaveSuccess,
    },
    props: {
        redirectAfterCreate: {
            type: Boolean,
            default: false,
        },
        newData: {  // passed directly to newData prop on ContactDetailForm (Create version only, not update)
            // should be object like this:  {company: companyId}
            type: Object,
            required: false,
        },
        hideCompanyInput: {  // set to true if passing in companyId with newData, only applies to Create version
            type: Boolean,
            default: false,
        },
        companyData: {  // passed directly to companyData prop on ContactDetailForm (only relevant for create, not update)
            // should be full company object, with ID and name at least
            // only used to HARDCODE the companyData initial values, not for passing in value from existing TU
            type: Object,
            required: false,
        },
        allowCompanyChange: {
            // IF companyData is set / passed in, but you still want to allow the company to be changed from that preset value, set this to True -- only applies to add/create
            type: Boolean,
            default: false,
        },
        enabledRoleOptionsCreate: {  // TU roles that current TU is allowed to manage
            // only pass in the ones that make sense for adding new TUs (ie: should have can_login=True)
            // array of objects (keys 'id' and 'name') to pass to select for selecting a TenantUserRole
            type: Array,
            required: true,
        },
        enabledRoleOptionsUpdate: {  // TU roles that current TU is allowed to manage
            // pass in all internal roles that make sense for update... including internal_noaccess
            // should include at least { id, name } 
            type: Array,
            required: true,
        },
        allRoleOptions: {  // all possible TU roles -- array of objects (keys 'id' and 'name') to pass to select for selecting a TenantUserRole
            // changed things so we don't use this... annoying to show irrelevant disabled roles
            // FEB 22 2022 -- STILL NEED THIS!  So we can display roles even for the current TU if they are superadmin
            type: Array,
            required: true,
        }
    },
    data: function() {
        return {
            createModal: false,
            updateModal: false,
            editMode: false,
            updateItemId: null,
            showUpdateSuccess: false,  
            showCreateSuccess: false,
            updateSuccessMessage: "Updated successfully",
            createSuccessMessage: "User saved",
            createSuccessMessageRedirect: "User saved.  Let's add some details...",
            successMessageTime: 2000,
        }
    },
    computed: {
    },
    methods: {
        // below are 2 main trigger functions -- can call these publicly
        startUpdate: function(updateId) {
            this.updateItemId = updateId;
            this.updateModal = true;
        },
        startCreate: function() {
            this.createModal = true;
        },
        handleUpdateComplete: function(updatedObject) {
            // updatedObject MAY be the newly updated object returned from API (in response.data) - depends on your endpoint
            console.log('Updated response data below');
            console.log(updatedObject);
            this.showUpdateSuccess = true;
            this.$emit('update-complete', updatedObject);  // relay the update-complete event from form
            setTimeout(() => {
                // should we redirect to detail page after update?  if so, put here
                this.updateModal = false;
                this.$emit('update-modal-hide');  // tell parent we're closing
            }, this.successMessageTime);
        },
        onUpdateModalHidden: function() {
            this.editMode = false;
            this.showUpdateSuccess = false;
            this.$emit('update-modal-hidden');  // tell the parent we're hidden
        },
        handleCreateComplete: function(newObject) {
            // newObject should be the newly created object returned from API (in response.data)
            console.log('From parent component, we got this newObject passed up from save-complete event');
            console.log(newObject);
            this.showCreateSuccess = true;
            this.$emit('create-complete', newObject);  // relay the save-complete event from form
            setTimeout(() => {  // after the timeout, do the redirect if we have the URL
                let redirectUrl = newObject.company_url;
                if (redirectUrl && this.redirectAfterCreate) {
                    window.location.href = redirectUrl;  // THE REDIRECT
                } else {  // if we didn't have redirect URL just hide the modal and emit event to tell parent we're done
                    this.createModal = false;
                    this.$emit('create-modal-hide');  // tell parent we're closing modal
                }
            }, this.successMessageTime);
        },
        onCreateModalHidden: function() {
            this.editMode = false;  // just in case?
            this.showCreateSuccess = false;
            this.$emit('create-modal-hidden');  // tell the parent we're hidden
        },
        handleInviteUpdate: function(updatedTuObj) {
            // called when we update the TU's portal invite status, like when sending invite renewal or reminder email
            // just emit something to update a list on parent, pass up the object you got
            this.$emit('update-complete', updatedTuObj);  // same event that you use when updating an object... its the same really
        },
        // NEXT 4 METHODS CALL METHOD ON CHILD FORM COMPONENT
        enterEditMode: function() {
            // set local edit mode and call cancelEditMode on child form
            //this.editMode = true;
            const editModeEntered = this.$refs.updateForm.enterEditMode();  // this now returns Boolean, as of Feb 25 2022
            this.editMode = editModeEntered;
        },
        cancelEditMode: function() {
            // set local edit mode and call cancelEditMode on child form
            this.editMode = false;
            this.$refs.updateForm.cancelEditMode();
        },
        createItemForm: function() {
            this.$refs.createForm.saveNewItem();
        },
        updateItemForm: function() {
            // call update method on child form
            // no need to set local editMode value here... let events from child component set that
            // but make sure you watch for them with @edit-on and @edit-off
            this.$refs.updateForm.updateItemData();
        },
    }
}
</script>

<style>

</style>